import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Plus, Search, Crown, Users, ChevronRight, Bell, Star, X, Check, User, ArrowLeft, Briefcase } from 'lucide-react';

import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';

const MultiCompanyNavigationSidebar = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState(new Set());
  const [sortBy, setSortBy] = useState('recent'); // 'recent', 'alphabetical', 'activity'

  // Mock user data
  const userData = {
    name: 'John Doe',
    email: 'john.doe@example.com',
    avatar: 'JD',
    currentRole: 'Job Seeker'
  };

  // Mock companies data with expanded information
  const [companies, setCompanies] = useState([
    {
      id: '1',
      name: 'TechCorp Inc.',
      logo: 'TC',
      industry: 'Technology',
      memberCount: 12,
      role: 'admin',
      unreadCount: 3,
      lastActivity: '2 hours ago',
      activeJobs: 5,
      recentApplications: 23,
      isFavorite: true,
      joinedDate: '2024-01-15'
    },
    {
      id: '2',
      name: 'StartupXYZ',
      logo: 'SX',
      industry: 'Fintech',
      memberCount: 8,
      role: 'recruiter',
      unreadCount: 1,
      lastActivity: '1 day ago',
      activeJobs: 2,
      recentApplications: 8,
      isFavorite: false,
      joinedDate: '2024-02-20'
    },
    {
      id: '3',
      name: 'BigTech Co.',
      logo: 'BT',
      industry: 'Software',
      memberCount: 45,
      role: 'admin',
      unreadCount: 0,
      lastActivity: '3 days ago',
      activeJobs: 12,
      recentApplications: 67,
      isFavorite: true,
      joinedDate: '2023-11-10'
    },
    {
      id: '4',
      name: 'DesignStudio Pro',
      logo: 'DS',
      industry: 'Design',
      memberCount: 6,
      role: 'recruiter',
      unreadCount: 5,
      lastActivity: '30 minutes ago',
      activeJobs: 3,
      recentApplications: 12,
      isFavorite: false,
      joinedDate: '2024-03-05'
    }
  ]);

  // Handle create company action from URL params
  useEffect(() => {
    if (searchParams?.get('action') === 'create') {
      setShowCreateModal(true);
    }
  }, [searchParams]);

  // Filter and sort companies
  const filteredAndSortedCompanies = companies?.filter(company => 
      company?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      company?.industry?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    )?.sort((a, b) => {
      switch (sortBy) {
        case 'alphabetical':
          return a?.name?.localeCompare(b?.name);
        case 'activity':
          return new Date(b?.lastActivity) - new Date(a?.lastActivity);
        case 'recent':
        default:
          return new Date(b?.joinedDate) - new Date(a?.joinedDate);
      }
    });

  const toggleFavorite = (companyId) => {
    setCompanies(prev => 
      prev?.map(company => 
        company?.id === companyId 
          ? { ...company, isFavorite: !company?.isFavorite }
          : company
      )
    );
  };

  const getRoleIcon = (role) => {
    return role === 'admin' ? <Crown className="w-3 h-3" /> : <Users className="w-3 h-3" />;
  };

  const getRoleColor = (role) => {
    return role === 'admin' ? 'text-yellow-600 bg-yellow-50' : 'text-blue-600 bg-blue-50';
  };

  const CreateCompanyModal = () => {
    const [companyName, setCompanyName] = useState('');
    const [industry, setIndustry] = useState('');
    const [companySize, setCompanySize] = useState('');

    const handleCreateCompany = () => {
      if (companyName?.trim()) {
        const newCompany = {
          id: String(companies?.length + 1),
          name: companyName,
          logo: companyName?.charAt(0)?.toUpperCase(),
          industry: industry || 'Technology',
          memberCount: 1,
          role: 'admin',
          unreadCount: 0,
          lastActivity: 'just now',
          activeJobs: 0,
          recentApplications: 0,
          isFavorite: false,
          joinedDate: new Date()?.toISOString()?.split('T')?.[0]
        };
        
        setCompanies(prev => [...prev, newCompany]);
        setShowCreateModal(false);
        navigate(`/company-dashboard-hub/${newCompany?.id}`);
      }
    };

    return (
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-background rounded-lg shadow-xl max-w-md w-full p-6"
              onClick={(e) => e?.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground">Create New Company</h2>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowCreateModal(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Company Name *
                  </label>
                  <Input
                    value={companyName}
                    onChange={(e) => setCompanyName(e?.target?.value)}
                    placeholder="Enter company name"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Industry
                  </label>
                  <select
                    value={industry}
                    onChange={(e) => setIndustry(e?.target?.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    <option value="">Select industry</option>
                    <option value="Technology">Technology</option>
                    <option value="Fintech">Fintech</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Education">Education</option>
                    <option value="Design">Design</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Company Size
                  </label>
                  <select
                    value={companySize}
                    onChange={(e) => setCompanySize(e?.target?.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    <option value="">Select size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-1000">201-1000 employees</option>
                    <option value="1000+">1000+ employees</option>
                  </select>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateCompany}
                  disabled={!companyName?.trim()}
                  className="flex-1"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Create Company
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Dashboard</span>
              </Button>
              <div className="h-6 w-px bg-border" />
              <h1 className="text-xl font-semibold text-foreground">
                Company Navigation
              </h1>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-8">
              {/* User Profile Section */}
              <div className="text-center pb-6 border-b border-border">
                <div className="w-16 h-16 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-3">
                  {userData?.avatar}
                </div>
                <h3 className="font-semibold text-foreground">{userData?.name}</h3>
                <p className="text-sm text-muted-foreground">{userData?.email}</p>
                <div className="flex items-center justify-center space-x-1 mt-2 text-sm">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">{userData?.currentRole}</span>
                </div>
              </div>

              {/* Navigation Actions */}
              <div className="py-6 space-y-3">
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="w-full flex items-center space-x-2"
                >
                  <Briefcase className="w-4 h-4" />
                  <span>Job Seeker Dashboard</span>
                </Button>
                
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="w-full flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create New Company</span>
                </Button>
              </div>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Search and Filter Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  placeholder="Search companies..."
                  className="pl-10"
                />
              </div>
              
              <div className="flex items-center space-x-3">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e?.target?.value)}
                  className="px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <option value="recent">Recently Joined</option>
                  <option value="alphabetical">Alphabetical</option>
                  <option value="activity">Recent Activity</option>
                </select>
              </div>
            </div>

            {/* Companies Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredAndSortedCompanies?.map(company => (
                <motion.div
                  key={company?.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Card className="p-6 hover:shadow-lg transition-all cursor-pointer relative">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-accent text-accent-foreground rounded-lg flex items-center justify-center text-lg font-bold">
                          {company?.logo}
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">{company?.name}</h3>
                          <p className="text-sm text-muted-foreground">{company?.industry}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e?.stopPropagation();
                            toggleFavorite(company?.id);
                          }}
                        >
                          <Star className={`w-4 h-4 ${company?.isFavorite ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`} />
                        </Button>
                        
                        {company?.unreadCount > 0 && (
                          <div className="flex items-center space-x-1">
                            <Bell className="w-4 h-4 text-red-500" />
                            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                              {company?.unreadCount}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Role</span>
                        <div className={`flex items-center space-x-1 px-2 py-1 rounded-full ${getRoleColor(company?.role)}`}>
                          {getRoleIcon(company?.role)}
                          <span className="text-xs font-medium capitalize">{company?.role}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Members</span>
                        <span className="font-medium">{company?.memberCount}</span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Active Jobs</span>
                        <span className="font-medium text-green-600">{company?.activeJobs}</span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Recent Apps</span>
                        <span className="font-medium text-blue-600">{company?.recentApplications}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <span className="text-xs text-muted-foreground">
                        Active {company?.lastActivity}
                      </span>
                      
                      <div className="flex space-x-2">
                        <Button
                          onClick={(e) => {
                            e?.stopPropagation();
                            navigate(`/company-dashboard-hub/${company?.id}`);
                          }}
                          size="sm"
                          className="flex items-center space-x-1"
                        >
                          <span>Switch Dashboard</span>
                          <ChevronRight className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Empty State */}
            {filteredAndSortedCompanies?.length === 0 && (
              <div className="text-center py-12">
                <Building2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {searchTerm ? 'No companies found' : 'No companies yet'}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {searchTerm 
                    ? `No companies match "${searchTerm}". Try adjusting your search.`
                    : 'Get started by creating your first company page.'
                  }
                </p>
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create Your First Company</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Create Company Modal */}
      <CreateCompanyModal />
    </div>
  );
};

export default MultiCompanyNavigationSidebar;